import { createSelector } from '@ngrx/store';
import {
  Customer,
  selectCustomerDetails,
  selectFormattedCustomerDetails,
} from '@modules/customer-store';
import { sumBy, uniqBy } from 'lodash-es';
import {
  BookingSummary,
  BookingSummaryItem,
  LoadingDetails,
  IBasketItemForGtmCheckout,
  ICustomerDetails,
  IDirectDebitDetails,
  IFlexiTicketSummary,
  IFlexiTicketSummaryWithQty,
  ISearchTicketResult,
  ISearchTicketResultWithQty,
  ITicketDescriptionFields,
  CustomerTypeIds,
  IDirectDebitBankDetails,
  IBasketWithDescriptions,
  EMPTY_CUSTOMER_DETAILS,
} from '@nx-ng/types';
import {
  selectAvailableFlexiSummary,
  selectAvailablePaygSummary,
  selectFareIdsAndDescriptions,
  selectSearchFaresMatchingSelections,
} from '@modules/ticket-fares-store';
import { Basket, BasketCheckoutRequest } from '../../client/src';
import { getCheckoutFeatureState, ICheckoutFeatureState } from '../../reducers';
import { ICheckoutState } from '../../reducers/basket';
import { calculateDirectDebitAmount, hasDirectDebit } from '../../util';
import {
  combineBasketTicketsWithDescription,
  formatBasketCheckoutRequest,
} from '../../util';
import {
  addQuantityInBasketToFlexiItems,
  addQuantityInBasketToSearchResults,
} from '../../util/add-fare-counts';
import { isValidBasket } from '../../util/basket-validation';
import { selectSavedBookingSummary } from '../booking-summary';
import { selectBankDetails, selectDirectDebitForm } from '../direct-debit';
import {
  annotateBasketWithDescriptions,
  buildBasketItemsForGtmCheckout,
  buildBookingSummaryPageDetails,
} from './basket.util';
import { BookingSummaryTicket } from '@nx-ng/types/booking-summary-ticket';
import { getLoadingDetails } from '@nx-ng/util/get-loading-details';

export const selectCheckoutState = createSelector(
  getCheckoutFeatureState,
  (state: ICheckoutFeatureState) => state?.checkout
);

export const selectError = createSelector(
  selectCheckoutState,
  (state: ICheckoutState) => state?.error
);

export const selectBasket = createSelector(
  selectCheckoutState,
  (state: ICheckoutState) => {
    return state?.basket;
  }
);

export const selectCheckoutLoading = createSelector(
  selectCheckoutState,
  (state: ICheckoutState): LoadingDetails =>
    getLoadingDetails(state?.loaded, 'loading_basket')
);

export const selectValidBasket = createSelector(
  selectBasket,
  (basket?: Basket) => basket && basket?.totalItems > 0
);

export const selectBasketId = createSelector(
  selectBasket,
  (basket?: Basket) => basket?.basketId
);

export const selectBasketLoadState = createSelector(
  selectCheckoutState,
  (state: ICheckoutState) => state?.loaded
);

export const selectBasketWithDescriptions = createSelector(
  selectBasket,
  selectFareIdsAndDescriptions,
  (
    basket?: Basket,
    ticketDescriptions?: ITicketDescriptionFields
  ): IBasketWithDescriptions | undefined => {
    return combineBasketTicketsWithDescription(basket, ticketDescriptions);
  }
);

export const selectFlexiFaresWithQuantity = createSelector(
  selectBasket,
  selectAvailableFlexiSummary,
  (
    basket?: Basket,
    flexifares?: IFlexiTicketSummary[]
  ): IFlexiTicketSummaryWithQty[] => {
    return addQuantityInBasketToFlexiItems({ basket, items: flexifares });
  }
);

export const selectPaygFaresWithQuantity = createSelector(
  selectBasket,
  selectAvailablePaygSummary,
  (
    basket?: Basket,
    flexifares?: IFlexiTicketSummary[]
  ): IFlexiTicketSummaryWithQty[] => {
    return addQuantityInBasketToFlexiItems({ basket, items: flexifares });
  }
);

export const selectSearchFaresWithQuantity = createSelector(
  selectBasket,
  selectSearchFaresMatchingSelections,
  (
    basket?: Basket,
    searchItems?: ISearchTicketResult[]
  ): ISearchTicketResultWithQty[] => {
    return addQuantityInBasketToSearchResults({ basket, items: searchItems });
  }
);

export const selectTotalBasketQuantityCount = createSelector(
  selectBasket,
  (basket?: Basket): number => {
    return sumBy(basket?.basketItems, (item) => item.quantity);
  }
);

export const selectTotalBasketCount = createSelector(
  selectBasket,
  (basket?: Basket): number => basket?.totalItems || 0
);

export const selectBookingSummaryItems = createSelector(
  selectBasket,
  selectFareIdsAndDescriptions,
  selectSavedBookingSummary,
  (
    basket: Basket | undefined,
    descriptions: ITicketDescriptionFields,
    savedBookingSummary: BookingSummary | undefined
  ): BookingSummaryItem[] => {
    if (basket) {
      return annotateBasketWithDescriptions({
        savedBookingSummary,
        basket,
        descriptions,
      });
    }
    return [];
  }
);

export const selectBookingSummaryTickets = createSelector(
  selectBookingSummaryItems,
  (items: BookingSummaryItem[]): BookingSummaryTicket[] => {
    return uniqBy(items, (item) => item.ticket.id).map(
      (item: BookingSummaryItem) => item.ticket
    );
  }
);

export const selectBookingSummaryPageDetails = createSelector(
  selectBookingSummaryItems,
  selectFormattedCustomerDetails,
  selectSavedBookingSummary,
  (
    bookingItems: BookingSummaryItem[],
    customerDetails: ICustomerDetails | undefined,
    savedBookingSummary: BookingSummary | undefined
  ): BookingSummary =>
    buildBookingSummaryPageDetails({
      bookingItems,
      customerDetails,
      savedBookingSummary,
    })
);

export const selectToPayBasketTotal = createSelector(
  selectBasket,
  (basket?: Basket): number => {
    return basket?.toPayBasketTotal || 0;
  }
);

export const selectSubtotalBasketPrice = createSelector(
  selectBasket,
  (basket?: Basket): number => {
    return basket?.basketTotal || 0;
  }
);

export const selectTotalDirectDebit = createSelector(
  selectBasket,
  (basket?: Basket): number => calculateDirectDebitAmount(basket)
);

export const selectHasDirectDebit = createSelector(
  selectBasket,
  (basket?: Basket): boolean => hasDirectDebit(basket)
);

export const selectIsValidBasket = createSelector(
  selectBasket,
  (basket?: Basket): boolean => {
    return isValidBasket(basket);
  }
);

export const selectBasketCheckoutRequest = createSelector(
  selectDirectDebitForm,
  selectBankDetails,
  selectBookingSummaryPageDetails,
  selectCustomerDetails,
  selectFareIdsAndDescriptions,
  (
    directDebitForm: IDirectDebitDetails | undefined,
    bankDetails: IDirectDebitBankDetails | undefined,
    bookingSummary: BookingSummary,
    accountHolder: Customer | undefined,
    fares: ITicketDescriptionFields
  ): BasketCheckoutRequest => {
    return formatBasketCheckoutRequest({
      directDebitForm,
      bankDetails,
      bookingSummary,
      accountHolder,
      fares,
    });
  }
);

export const selectChildTicketIds = createSelector(
  selectBookingSummaryItems,
  (items: BookingSummaryItem[]): number[] => {
    return items
      .filter((item) => item.customerType === CustomerTypeIds.Child)
      .map((item) => item.ticket.id);
  }
);

export const selectHasChildTickets = createSelector(
  selectChildTicketIds,
  (childTicketIds: number[]): boolean => {
    return childTicketIds.length > 0;
  }
);

export const selectBasketItemsForGtmCheckout = createSelector(
  selectBasket,
  selectBookingSummaryItems,
  (
    basket?: Basket,
    bookingItems: BookingSummaryItem[] = []
  ): IBasketItemForGtmCheckout[] =>
    buildBasketItemsForGtmCheckout({
      basket,
      bookingItems,
    })
);
